// Frontend mirror of the actuals error categorizers used by the Actuals Troubleshooter:
// - fleet-int-api/slim/actuals_error_categories.py (Coach Manager + FastTrak messages)
// - tbn-integration/slim/actuals_error_categories.py (TBN messages)
// Keep pattern/category changes in sync with those files.
//
// The same raw string can mean different things per pipeline (e.g. "missing finish time"
// is a booking-data problem in CM but a missing-GPS-arrival problem in TBN), so the
// categorizer is source-aware ('cm' | 'fasttrak' | 'tbn').

export const GROUP_LABELS = {
  booking_data: 'Booking data issues (fix in your booking system)',
  telematics_matching: 'Telematics matching issues',
  other: 'Other processing issues',
}

// Order matters: first matching pattern wins.
const CM_FASTTRAK_CATEGORIES = [
  {
    id: 'invalid_scheduled_times',
    category: 'Invalid scheduled times (midnight placeholder)',
    group: 'booking_data',
    explanation: "The movement's start or finish time is set to midnight (00:00), which is treated as a placeholder rather than a real time. Actuals cannot be matched without a valid scheduled window.",
    suggested_fix: 'Enter the real start and finish times for the movement in your booking system instead of leaving them at 12:00 AM.',
    patterns: [/^invalid start time$/i, /^invalid finish time$/i],
  },
  {
    id: 'missing_vehicle_assignment',
    category: 'No vehicle assigned in booking',
    group: 'booking_data',
    explanation: "The booking does not include a fleet vehicle ID, so there is no way to look up the vehicle's GPS data in telematics.",
    suggested_fix: 'Assign a fleet vehicle (with its fleet ID) to the booking in your booking system before the trip runs.',
    patterns: [/^vehicleid not found in booking$/i, /^missing vehicle_name$/i],
  },
  {
    id: 'missing_locations',
    category: 'Missing location coordinates in booking',
    group: 'booking_data',
    explanation: "The movement's start or finish location has no latitude/longitude, so vehicle GPS positions cannot be matched against it.",
    suggested_fix: "Ensure the booking's start and finish addresses are complete so they resolve to map coordinates.",
    patterns: [
      /^cm: missing (start|stop) location lat\/long$/i,
      /^missing start location$/i,
      /^missing finish location$/i,
      /^missing pickup location$/i,
      /^missing leave location$/i,
      /^missing back location$/i,
    ],
  },
  {
    id: 'missing_scheduled_times',
    category: 'Missing scheduled times in booking',
    group: 'booking_data',
    explanation: 'The movement is missing its scheduled start, finish, pickup, or dropoff time entirely.',
    suggested_fix: 'Fill in the missing scheduled times on the booking in your booking system.',
    patterns: [
      /^missing start time$/i,
      /^missing finish time$/i,
      /^missing pickup time$/i,
      /^missing dropoff time$/i,
      /^missing scheduled_pickup_time or scheduled_end_time$/i,
    ],
  },
  {
    id: 'timing_sequence',
    category: 'Scheduled times out of order',
    group: 'booking_data',
    explanation: "The booking's scheduled times conflict with each other (e.g. the start time is after the pickup or finish time), so a valid trip window cannot be built.",
    suggested_fix: "Review the booking's scheduled times and correct any that are out of sequence or identical.",
    patterns: [
      /^start after arrival$/i,
      /^start after pickup$/i,
      /^pickup time after finish time$/i,
      /^start time after finish time$/i,
      /^start time equal to finish time$/i,
      /^pickup time after dropoff time$/i,
      /^pickup time equal to dropoff time$/i,
    ],
  },
  {
    id: 'geocoding_failed',
    category: 'Address could not be geocoded',
    group: 'booking_data',
    explanation: "The booking's start or finish address could not be converted to map coordinates, so GPS positions cannot be matched against it.",
    suggested_fix: "Check the booking's addresses for typos or incomplete information so they resolve on a map.",
    patterns: [/^unable to geocode (starting \(from\)|finish \(to\)) address for actuals$/i],
  },
  {
    id: 'vehicle_not_in_telematics',
    category: 'Vehicle not matched in telematics',
    group: 'telematics_matching',
    explanation: 'The vehicle ID on the booking does not match any vehicle in the telematics provider. This is often a naming mismatch, a vehicle without a device, or a placeholder code (e.g. TRAINING).',
    suggested_fix: 'Confirm the vehicle is named consistently in both your booking system and the telematics provider, and exclude placeholder vehicle codes from actuals processing.',
    patterns: [
      /^no vehicle found in telematics for/i,
      /^unable to match vehicle name in telematics/i,
    ],
  },
  {
    id: 'missing_actual_start_and_finish',
    category: 'Could not determine actual start and finish times',
    group: 'telematics_matching',
    explanation: 'No usable GPS activity was found to establish either the departure or arrival time for the trip.',
    suggested_fix: "Verify the telematics device was powered and reporting during the trip window, and that the booking's scheduled window roughly matches when the vehicle actually ran.",
    patterns: [
      /^could not determine actual start and finish times$/i,
      /^could not determine valid actual start and finish times$/i,
      /^unable to find (valid )?actual start & end times$/i,
    ],
  },
  {
    id: 'missing_actual_start',
    category: 'Could not determine actual start time',
    group: 'telematics_matching',
    explanation: 'No GPS departure event was found near the scheduled start location and time, so the actual start time could not be calculated.',
    suggested_fix: "Check that the telematics device was reporting before departure and that the booking's start location and time match where and when the vehicle actually left.",
    patterns: [
      /^could not determine actual start time$/i,
      /^missing actual start time$/i,
      /^unable to find actual start time$/i,
      /^could not determine vehicle departure time from starting location$/i,
    ],
  },
  {
    id: 'missing_actual_finish',
    category: 'Could not determine actual finish time',
    group: 'telematics_matching',
    explanation: 'No GPS arrival event was found near the scheduled finish location and time, so the actual finish time could not be calculated.',
    suggested_fix: "Check that the telematics device was reporting through the end of the trip and that the booking's finish location and time match where and when the vehicle actually arrived.",
    patterns: [
      /^could not determine actual finish time$/i,
      /^missing actual finish time$/i,
      /^unable to find actual end time$/i,
      /^could not determine vehicle arrival time at finish location$/i,
    ],
  },
  {
    id: 'no_gps_data',
    category: 'No GPS activity found',
    group: 'telematics_matching',
    explanation: 'The telematics provider returned no GPS locations or vehicle/driver activity in the trip window.',
    suggested_fix: "Verify the vehicle's telematics device was installed, powered, and reporting during the trip window.",
    patterns: [
      /^no gps locations in window/i,
      /^no actual vehicle or driver activity found$/i,
    ],
  },
  {
    id: 'no_overlapping_trips',
    category: 'No GPS trips matching the scheduled window',
    group: 'telematics_matching',
    explanation: "The telematics provider has trip data for the vehicle, but none of it overlaps the booking's scheduled window.",
    suggested_fix: "Confirm the booking's scheduled times reflect when the vehicle actually ran, and that the correct vehicle was assigned.",
    patterns: [/^unable to find trips that overlap/i],
  },
  {
    id: 'driver_not_in_telematics',
    category: 'Driver not matched in telematics',
    group: 'telematics_matching',
    explanation: 'The driver assigned on the booking does not exist as a profile in the telematics provider, or the name does not match any driver there. Driver-based actual times cannot be calculated until the driver is set up in telematics.',
    suggested_fix: "Add the driver to your telematics provider, or ensure the driver's name in your booking system matches their telematics profile exactly.",
    patterns: [
      /^unable to find driver in telematics/i,
      /^cm: driver not found in samsara for/i,
      /^driver not found in (telematics|samsara)/i,
      /^could not determine actual start time: driver not found in telematics/i,
      /^could not determine actual finish time: driver not found in telematics/i,
    ],
  },
  {
    id: 'driver_hos_wrong_vehicle',
    category: 'Driver HOS logged on a different vehicle',
    group: 'telematics_matching',
    explanation: 'The driver has hours-of-service activity in telematics, but it is associated with a different vehicle than the one assigned on the booking.',
    suggested_fix: 'Verify the correct vehicle was assigned on the booking, or have the driver log in against the assigned vehicle in telematics.',
    patterns: [
      /^driver HOS logged on vehicle .+ instead of assigned vehicle/i,
      /^driver HOS logs found for different vehicle: .+/i,
    ],
  },
  {
    id: 'driver_hos_no_assigned_overlap',
    category: 'No driver HOS on assigned vehicle during planned trip',
    group: 'telematics_matching',
    explanation: "The driver has no on-duty or driving HOS activity on the assigned vehicle that overlaps the booking's planned start and finish times.",
    suggested_fix: 'Have the driver log in to the assigned vehicle in telematics for this trip, or verify the correct vehicle and driver are assigned on the booking.',
    patterns: [
      /^no driver HOS events for the assigned vehicle$/i,
    ],
  },
  {
    id: 'driver_hos',
    category: 'Driver login/logout (HOS) not found',
    group: 'telematics_matching',
    explanation: "The driver's hours-of-service login or logout events could not be found on the assigned vehicle around the trip window.",
    suggested_fix: 'Verify the driver logged in and out of the telematics device for this trip on the assigned vehicle.',
    patterns: [
      /^missing driver login/i,
      /^missing driver logout/i,
      /^missing driver start time$/i,
      /^missing driver finish time$/i,
      /^missing driver stop time$/i,
      /^can't find valid driver login and logout/i,
    ],
  },
  {
    id: 'planned_driver_overlap',
    category: 'Planned times overlap other bookings (driver)',
    group: 'booking_data',
    explanation: "This booking's scheduled window overlaps another booking for the same driver. Actuals cannot be calculated until the schedule conflict is resolved in your booking system.",
    suggested_fix: 'Adjust the booking schedule so this driver is not assigned to overlapping trips, or reassign one of the conflicting bookings.',
    patterns: [
      /^planned times overlap with other bookings for driver$/i,
      /^planned times overlap with another driver's booking$/i,
      /^planned overlap with adjacent bookings for the same driver$/i,
      /^planned overlap with adjacent driver bookings$/i,
    ],
  },
  {
    id: 'planned_vehicle_overlap',
    category: 'Planned times overlap other bookings (vehicle)',
    group: 'booking_data',
    explanation: "This booking's scheduled window overlaps another booking for the same vehicle. Actuals cannot be calculated until the schedule conflict is resolved in your booking system.",
    suggested_fix: 'Adjust the booking schedule so this vehicle is not assigned to overlapping trips, or reassign one of the conflicting bookings.',
    patterns: [
      /^planned times overlap with other bookings for vehicle$/i,
      /^planned times overlap with another vehicle's booking$/i,
      /^planned overlap with adjacent bookings for the same vehicle$/i,
    ],
  },
  {
    id: 'processing_error',
    category: 'Processing error',
    group: 'other',
    explanation: "An unexpected error occurred while processing this movement's actuals.",
    suggested_fix: 'Contact support with the booking ID so the processing error can be investigated.',
    patterns: [
      /^movement skipped/i,
      /^exception:/i,
      /^no telematics client/i,
    ],
  },
]

const TBN_CATEGORIES = [
  {
    id: 'missing_driver_assignment',
    category: 'No driver assigned in booking',
    group: 'booking_data',
    explanation: 'The dispatch has no driver on it, so driver HOS times cannot be looked up in telematics.',
    suggested_fix: 'Assign a driver to the dispatch in your booking system before the trip runs.',
    patterns: [/^driver is missing$/i],
  },
  {
    id: 'missing_vehicle_assignment',
    category: 'No vehicle assigned in booking',
    group: 'booking_data',
    explanation: "The dispatch has no vehicle on it, so there is no way to look up the vehicle's GPS data in telematics.",
    suggested_fix: 'Assign a fleet vehicle to the dispatch in your booking system before the trip runs.',
    patterns: [/^vehicle is missing$/i],
  },
  {
    id: 'missing_locations',
    category: 'Missing location coordinates in booking',
    group: 'booking_data',
    explanation: "The booking's start or stop location has no latitude/longitude, so vehicle GPS positions cannot be matched against it.",
    suggested_fix: "Ensure the booking's start and stop addresses are complete so they resolve to map coordinates.",
    patterns: [
      /^missing start location lat\/lo(n|ng)$/i,
      /^missing stop location lat\/lo(n|ng)$/i,
    ],
  },
  {
    id: 'missing_scheduled_times',
    category: 'Missing scheduled times in booking',
    group: 'booking_data',
    explanation: 'The vehicle assignment is missing its scheduled start or finish time, so a trip window cannot be built.',
    suggested_fix: 'Fill in the missing scheduled times on the vehicle assignment in your booking system.',
    patterns: [
      /^vehicleassignment skipped with missing start time$/i,
      /^vehicleassignment skipped with missing finish time$/i,
    ],
  },
  {
    id: 'timing_sequence',
    category: 'Scheduled times out of order',
    group: 'booking_data',
    explanation: "The booking's planned departure time is after its planned finish time, so a valid trip window cannot be built.",
    suggested_fix: "Review the booking's scheduled times and correct any that are out of sequence.",
    patterns: [/^invalid booking times/i],
  },
  {
    id: 'vehicle_not_in_telematics',
    category: 'Vehicle not matched in telematics',
    group: 'telematics_matching',
    explanation: 'The vehicle on the dispatch does not match any vehicle in the telematics provider. This is often a naming mismatch, a vehicle without a device, or a placeholder code.',
    suggested_fix: 'Confirm the vehicle is named consistently in both your booking system and the telematics provider, and exclude placeholder vehicle codes from actuals processing.',
    patterns: [/^no vehicle found in telematics for/i],
  },
  {
    id: 'missing_actual_start_and_finish',
    category: 'Could not determine actual start and finish times',
    group: 'telematics_matching',
    explanation: 'No usable GPS activity was found to establish either the departure or arrival time for the trip.',
    suggested_fix: "Verify the telematics device was powered and reporting during the trip window, and that the booking's scheduled window roughly matches when the vehicle actually ran.",
    patterns: [
      /^could not determine actual start and finish times$/i,
      /^could not determine valid actual start and finish times$/i,
      /^unable to find (valid )?actual start & end times$/i,
    ],
  },
  {
    id: 'missing_actual_start',
    category: 'Could not determine actual start time',
    group: 'telematics_matching',
    explanation: 'No GPS departure event was found near the scheduled start location and time, so the actual start time could not be calculated.',
    suggested_fix: "Check that the telematics device was reporting before departure and that the booking's start location and time match where and when the vehicle actually left.",
    patterns: [
      /^could not determine actual start time$/i,
      /^missing actual start time$/i,
    ],
  },
  {
    id: 'missing_actual_finish',
    category: 'Could not determine actual finish time',
    group: 'telematics_matching',
    explanation: 'No GPS arrival event was found near the scheduled stop location and time, so the actual finish time could not be calculated.',
    suggested_fix: "Check that the telematics device was reporting through the end of the trip and that the booking's stop location and time match where and when the vehicle actually arrived.",
    patterns: [
      /^could not determine actual finish time$/i,
      /^missing finish time$/i,
    ],
  },
  {
    id: 'no_overlapping_trips',
    category: 'No GPS trips matching the scheduled window',
    group: 'telematics_matching',
    explanation: "The telematics provider has trip data for the vehicle, but none of it overlaps the booking's scheduled window.",
    suggested_fix: "Confirm the booking's scheduled times reflect when the vehicle actually ran, and that the correct vehicle was assigned.",
    patterns: [/^unable to find trips that overlap/i],
  },
  {
    id: 'driver_not_in_telematics',
    category: 'Driver not matched in telematics',
    group: 'telematics_matching',
    explanation: 'The driver assigned on the dispatch does not exist as a profile in the telematics provider, or the name does not match any driver there. Driver-based actual times cannot be calculated until the driver is set up in telematics.',
    suggested_fix: "Add the driver to your telematics provider, or ensure the driver's name in your booking system matches their telematics profile exactly.",
    patterns: [
      /^unable to find driver in telematics/i,
      /^cm: driver not found in samsara for/i,
      /^driver not found in (telematics|samsara)/i,
      /^could not determine actual start time: driver not found in telematics/i,
      /^could not determine actual finish time: driver not found in telematics/i,
    ],
  },
  {
    id: 'driver_hos_wrong_vehicle',
    category: 'Driver HOS logged on a different vehicle',
    group: 'telematics_matching',
    explanation: 'The driver has hours-of-service activity in telematics, but it is associated with a different vehicle than the one assigned on the dispatch.',
    suggested_fix: 'Verify the correct vehicle was assigned on the dispatch, or have the driver log in against the assigned vehicle in telematics.',
    patterns: [
      /^driver HOS logged on vehicle .+ instead of assigned vehicle/i,
      /^driver HOS logs found for different vehicle: .+/i,
    ],
  },
  {
    id: 'driver_hos_no_assigned_overlap',
    category: 'No driver HOS on assigned vehicle during planned trip',
    group: 'telematics_matching',
    explanation: "The driver has no on-duty or driving HOS activity on the assigned vehicle that overlaps the booking's planned start and finish times.",
    suggested_fix: 'Have the driver log in to the assigned vehicle in telematics for this trip, or verify the correct vehicle and driver are assigned on the booking.',
    patterns: [
      /^no driver HOS events for the assigned vehicle$/i,
    ],
  },
  {
    id: 'driver_hos',
    category: 'Driver login/logout (HOS) not found',
    group: 'telematics_matching',
    explanation: "The driver's hours-of-service login or logout events could not be found on the assigned vehicle around the trip window.",
    suggested_fix: 'Verify the driver logged in and out of the telematics device for this trip on the assigned vehicle.',
    patterns: [
      /^missing driver login/i,
      /^missing driver logout/i,
      /^missing driver start time$/i,
      /^missing driver finish time$/i,
      /^missing driver stop time$/i,
      /^can't find valid driver login and logout/i,
    ],
  },
  {
    id: 'hos_planned_time_violation',
    category: 'Driver HOS times too far from planned schedule',
    group: 'telematics_matching',
    explanation: "The driver's HOS clock-in or clock-out time was found, but it differs from the booking's planned start or finish time by more than the allowed tolerance, so it was rejected as an unreliable actual time.",
    suggested_fix: "Confirm the driver logged in/out for this specific trip rather than an earlier or later one, and that the booking's scheduled times reflect when the trip was actually planned to run.",
    patterns: [
      /^driver hos times too far from planned trip times$/i,
    ],
  },
  {
    id: 'hos_vehicle_anchor_mismatch',
    category: 'Driver HOS times inconsistent with vehicle GPS times',
    group: 'telematics_matching',
    explanation: "The driver's HOS login/logout times don't line up with the vehicle's own GPS departure/arrival times for this trip (for example, HOS login well after the vehicle already left), so the HOS-based times were rejected as unreliable.",
    suggested_fix: "Verify the driver logged in to the vehicle at the start of the trip rather than partway through, and that the assigned vehicle's GPS activity matches this trip.",
    patterns: [
      /^driver hos times inconsistent with vehicle trip milestones$/i,
    ],
  },
  {
    id: 'milestone_sequence_violation',
    category: 'Trip milestone times out of order',
    group: 'telematics_matching',
    explanation: 'The calculated actual milestones for this trip (such as HOS login, ignition on, or GPS departure) are not in chronological order, so the actual start/finish times were discarded as unreliable.',
    suggested_fix: "Check for stale or duplicate telematics events (such as a leftover login from a previous trip) around this trip's window.",
    patterns: [
      /^trip milestone times out of expected order$/i,
    ],
  },
  {
    id: 'cancelled_booking',
    category: 'Booking or vehicle assignment was cancelled',
    group: 'other',
    explanation: 'The booking or its vehicle assignment was cancelled, so actuals were not processed for it.',
    suggested_fix: 'No action needed unless this booking should not have been cancelled.',
    patterns: [
      /^booking was cancelled$/i,
      /^vehicleassignment was cancelled$/i,
    ],
  },
  {
    id: 'processing_error',
    category: 'Processing error',
    group: 'other',
    explanation: "An unexpected error occurred while processing this booking's actuals.",
    suggested_fix: 'Contact support with the booking ID so the processing error can be investigated.',
    patterns: [
      /^vehicleassignment skipped .* with error/i,
      /^movement skipped/i,
      /^error calculating total duration/i,
    ],
  },
]

const OTHER_CATEGORY = {
  id: 'other',
  category: 'Other',
  group: 'other',
  explanation: 'An error that does not match a known category.',
  suggested_fix: 'Contact support with the booking ID and the error message for investigation.',
}

export function categorizeError(message, source) {
  const msg = String(message || '').trim()
  const categories = source === 'tbn' ? TBN_CATEGORIES : CM_FASTTRAK_CATEGORIES
  for (const definition of categories) {
    for (const pattern of definition.patterns) {
      if (pattern.test(msg))
        return definition
    }
  }
  return OTHER_CATEGORY
}

// Returns the list of known error categories for a source as { id, category, group },
// including the catch-all "Other" category. Used to label category-filter dropdowns.
export function listErrorCategories(source) {
  const categories = source === 'tbn' ? TBN_CATEGORIES : CM_FASTTRAK_CATEGORIES
  const list = categories.map(c => ({ id: c.id, category: c.category, group: c.group }))
  list.push({ id: OTHER_CATEGORY.id, category: OTHER_CATEGORY.category, group: OTHER_CATEGORY.group })
  return list
}
